import React from 'react';
import {
  Alert, Box, Button, Grid, Paper, Stack, Tab, Tabs, TextField, Typography
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { GridColDef } from '@mui/x-data-grid';
import { FGrid, FGridMoneyCell } from '../grid';
import { FWorkbenchShell } from '../workbench';
import {
  acceptBudget,
  adjustBudget,
  createFromReview,
  findDeterminantBudgets,
  loadBudgetReport,
  releaseBudget,
  returnBudget,
  validateBudget,
} from './determinantBudget.apis';

type State = {
  rows:any[]; loading:boolean; error:string; success:string;
  tab:string; selected:any|null; validation:any; report:any;
  planningcycleid:string; executivereviewid:string;
  name:string; description:string;
  adjustmentamount:string; reserveamount:string; notes:string;
};

class DeterminantBudgetWorkbench extends React.Component<any,State> {
  state:State = {
    rows:[],loading:false,error:'',success:'',
    tab:'overview',selected:null,validation:{},report:{},
    planningcycleid:'',executivereviewid:'',
    name:'Determinant Budget',description:'',
    adjustmentamount:'0',reserveamount:'0',notes:'',
  };

  columns:GridColDef[] = [
    {field:'code',headerName:'Code',width:180},
    {field:'name',headerName:'Name',flex:1,minWidth:240},
    {field:'planningcyclecode',headerName:'Planning Cycle',width:170},
    {field:'executivereviewcode',headerName:'Executive Review',width:180},
    {field:'scenariocode',headerName:'Scenario',width:140},
    {field:'computedpartialresult',headerName:'Trade Surplus',width:150,renderCell:FGridMoneyCell},
    {field:'computedeconomiccapacity',headerName:'Capacity',width:150,renderCell:FGridMoneyCell},
    {field:'planningenvelope',headerName:'Planning Envelope',width:170,renderCell:FGridMoneyCell},
    {field:'statecode',headerName:'State',width:190},
  ];

  componentDidMount(){ this.load(); }

  load = async () => {
    this.setState({loading:true,error:''});
    try {
      const rows = await findDeterminantBudgets({
        planningcycleid:this.state.planningcycleid ? Number(this.state.planningcycleid) : null,
        pagesize:50,
      });
      this.setState({rows});
    } catch(err:any) {
      this.setState({error:err.message || String(err)});
    } finally {
      this.setState({loading:false});
    }
  };

  create = async () => {
    await createFromReview({
      executivereviewid:Number(this.state.executivereviewid),
      name:this.state.name,
      description:this.state.description,
    });
    this.setState({success:'Determinant Budget created from Executive Review.'});
    await this.load();
  };

  selectRow = async (row:any) => {
    const [validation,report] = await Promise.all([
      validateBudget(row.rid),
      loadBudgetReport(row.rid),
    ]);
    this.setState({
      selected:row,
      validation,
      report,
      adjustmentamount:String(row.adjustmentamount || 0),
      reserveamount:String(row.reserveamount || 0),
      notes:row.adjustmentreason || '',
    });
  };

  saveAdjustment = async () => {
    if (!this.state.selected) return;
    await adjustBudget({
      rid:this.state.selected.rid,
      adjustmentamount:this.state.adjustmentamount,
      reserveamount:this.state.reserveamount,
      adjustmentreason:this.state.notes,
    });
    this.setState({success:'Management adjustment saved.'});
    await this.load();
  };

  accept = async () => {
    if (!this.state.selected) return;
    await acceptBudget(this.state.selected.rid,this.state.notes);
    this.setState({success:'Accepted for planning.'});
    await this.load();
  };

  sendBack = async () => {
    if (!this.state.selected) return;
    await returnBudget(this.state.selected.rid,this.state.notes);
    this.setState({success:'Returned for amendment.'});
    await this.load();
  };

  release = async () => {
    if (!this.state.selected) return;
    await releaseBudget(this.state.selected.rid,this.state.notes);
    this.setState({success:'Released to Activity Planning.'});
    await this.load();
  };

  renderOverview() {
    const r = this.state.selected;
    if (!r) return <Alert severity="info">Select a Determinant Budget.</Alert>;

    const items = [
      ['Planning Cycle',r.planningcyclecode],
      ['Executive Review',r.executivereviewcode],
      ['Scenario',r.scenariocode],
      ['Forecast Batch',r.forecastbatchcode || '-'],
      ['Trade Surplus',r.computedpartialresult],
      ['Economic Capacity',r.computedeconomiccapacity],
      ['Planning Envelope',r.planningenvelope],
      ['State',r.statecode],
    ];

    return (
      <Grid container spacing={2}>
        {items.map(([label,value])=>(
          <Grid size={{xs:12,md:3}} key={String(label)}>
            <Paper sx={{p:2}}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" sx={{fontWeight:800}}>{String(value ?? 0)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  renderEnvelope() {
    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{xs:12,md:3}}>
            <TextField label="Computed Envelope"
              value={this.state.selected?.computedplanningenvelope ?? 0}
              disabled fullWidth/>
          </Grid>
          <Grid size={{xs:12,md:3}}>
            <TextField label="Adjustment Amount"
              value={this.state.adjustmentamount}
              onChange={(e)=>this.setState({adjustmentamount:e.target.value})}
              fullWidth/>
          </Grid>
          <Grid size={{xs:12,md:3}}>
            <TextField label="Management Reserve"
              value={this.state.reserveamount}
              onChange={(e)=>this.setState({reserveamount:e.target.value})}
              fullWidth/>
          </Grid>
          <Grid size={{xs:12,md:3}}>
            <TextField label="Current Planning Envelope"
              value={this.state.selected?.planningenvelope ?? 0}
              disabled fullWidth/>
          </Grid>
        </Grid>

        <TextField label="Adjustment Reason"
          value={this.state.notes}
          onChange={(e)=>this.setState({notes:e.target.value})}
          multiline minRows={3} fullWidth/>

        <Button variant="contained" onClick={this.saveAdjustment}
          disabled={!this.state.selected}>
          Save Adjustment
        </Button>
      </Stack>
    );
  }

  renderManagement() {
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Management may adjust and accept the determinant budget before release to Activity Planning.
        </Alert>

        <TextField label="Management Notes"
          value={this.state.notes}
          onChange={(e)=>this.setState({notes:e.target.value})}
          multiline minRows={4} fullWidth/>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="success"
            onClick={this.accept} disabled={!this.state.selected}>
            Accept for Planning
          </Button>
          <Button variant="outlined" color="warning"
            onClick={this.sendBack} disabled={!this.state.selected}>
            Return
          </Button>
        </Stack>
      </Stack>
    );
  }

  renderValidation() {
    return (
      <Stack spacing={1}>
        <Alert severity={this.state.validation?.ready ? 'success' : 'warning'}>
          Readiness score: {this.state.validation?.score || 0}%
        </Alert>
        {(this.state.validation?.items || []).map((x:any)=>(
          <Alert key={x.code} severity={x.passed ? 'success' : 'warning'}>
            {x.label}
          </Alert>
        ))}
      </Stack>
    );
  }

  renderRelease() {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          Release is allowed only after management accepts the determinant budget.
        </Alert>
        <TextField label="Release Notes"
          value={this.state.notes}
          onChange={(e)=>this.setState({notes:e.target.value})}
          multiline minRows={3} fullWidth/>
        <Button variant="contained" onClick={this.release}
          disabled={!this.state.selected}>
          Release to Activity Planning
        </Button>
      </Stack>
    );
  }

  renderReport() {
    return (
      <Paper sx={{p:2}}>
        <Typography variant="h6" sx={{fontWeight:800}}>
          Determinant Budget Report Payload
        </Typography>
        <pre style={{whiteSpace:'pre-wrap'}}>
          {JSON.stringify(this.state.report || {},null,2)}
        </pre>
      </Paper>
    );
  }

  renderTab() {
    switch(this.state.tab) {
      case 'envelope': return this.renderEnvelope();
      case 'capacity':
        return <Alert severity="info">
          Render the frozen capacity snapshot and budget pressure here using the existing BIE capacity components.
        </Alert>;
      case 'management': return this.renderManagement();
      case 'validation': return this.renderValidation();
      case 'release': return this.renderRelease();
      case 'report': return this.renderReport();
      default: return this.renderOverview();
    }
  }

  render() {
    return (
      <FWorkbenchShell
        title="Determinant Budget"
        subtitle="Convert a recommended BIE scenario into the official planning envelope."
        icon={<AccountBalanceIcon/>}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.load}
      >
        <Stack spacing={2}>
          <Alert severity="info">
            The Executive Review recommendation is frozen as provenance. Management may adjust only the planning envelope.
          </Alert>

          <Grid container spacing={2}>
            <Grid size={{xs:12,md:2}}>
              <TextField size="small" label="Planning Cycle ID"
                value={this.state.planningcycleid}
                onChange={(e)=>this.setState({planningcycleid:e.target.value})}
                fullWidth/>
            </Grid>
            <Grid size={{xs:12,md:2}}>
              <TextField size="small" label="Executive Review ID"
                value={this.state.executivereviewid}
                onChange={(e)=>this.setState({executivereviewid:e.target.value})}
                fullWidth/>
            </Grid>
            <Grid size={{xs:12,md:3}}>
              <TextField size="small" label="Name"
                value={this.state.name}
                onChange={(e)=>this.setState({name:e.target.value})}
                fullWidth/>
            </Grid>
            <Grid size={{xs:12,md:3}}>
              <TextField size="small" label="Description"
                value={this.state.description}
                onChange={(e)=>this.setState({description:e.target.value})}
                fullWidth/>
            </Grid>
            <Grid size={{xs:12,md:2}}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={this.load}>Apply</Button>
                <Button variant="contained" onClick={this.create}>Create</Button>
              </Stack>
            </Grid>
          </Grid>

          <FGrid
            title="Determinant Budgets"
            subtitle="Working planning envelopes created from recommended Executive Reviews."
            rows={this.state.rows}
            columns={this.columns}
            loading={this.state.loading}
            height={400}
            showQuickFilter
            getRowId={(r:any)=>r.rid}
            onRowClick={(p:any)=>this.selectRow(p.row)}
            pageSizeOptions={[10,25,50]}
          />

          <Paper elevation={1}>
            <Tabs value={this.state.tab}
              onChange={(_,tab)=>this.setState({tab})}
              variant="scrollable" scrollButtons="auto">
              <Tab value="overview" label="Executive Summary"/>
              <Tab value="envelope" label="Budget Envelope"/>
              <Tab value="capacity" label="Capacity Analysis"/>
              <Tab value="management" label="Management Review"/>
              <Tab value="validation" label="Validation"/>
              <Tab value="release" label="Release"/>
              <Tab value="report" label="Report"/>
            </Tabs>
          </Paper>

          <Box>{this.renderTab()}</Box>
        </Stack>
      </FWorkbenchShell>
    );
  }
}

export default DeterminantBudgetWorkbench;
